'use client'

import { useEffect, useRef } from 'react'

import usePostNotificationEngagementMutation from '@/libs/apis/mutations/use-post-notification-engagement-mutation'
import useGetParams from '@/libs/hooks/use-get-params'

const NOTIFICATION_UTM_SOURCE = 'system-notification'

/**
 * Reports notification engagement to the backend when the user lands from a survey notification.
 *
 * Survey notifications point at /home, which has no survey-scoped request of its own, so the CTA
 * carries sub_survey_id/utm_source/utm_medium and this component turns them into one call. Mounted
 * in the main layout rather than a page so it covers every landing route.
 *
 * Kept separate from TrackClickNotification: that one only emits an Amplitude event and keys off a
 * different param.
 */
export default function NotificationEngagementTracker() {
  const {
    sub_survey_id: subSurveyId,
    utm_source: utmSource,
    utm_medium: utmMedium,
  } = useGetParams()
  const { mutate } = usePostNotificationEngagementMutation()

  // Keyed on the reported values, not a boolean: this component lives in the layout, so it stays
  // mounted across client-side navigation. Tapping a second notification (notification-item.tsx
  // soft-navigates via router.push) would otherwise be swallowed for the rest of the session.
  const reportedKey = useRef<string | null>(null)

  useEffect(() => {
    if (!subSurveyId) return
    // utm_source pins the click to our own notifications; utm_medium=email alone is the default for
    // any marketing blast, and a false first touch cannot be corrected afterwards.
    if (utmSource !== NOTIFICATION_UTM_SOURCE) return
    if (utmMedium !== 'email' && utmMedium !== 'push') return

    const key = `${subSurveyId}:${utmMedium}`
    if (reportedKey.current === key) return

    reportedKey.current = key
    mutate({ subSurveyId, channel: utmMedium })
  }, [subSurveyId, utmSource, utmMedium, mutate])

  return null
}
